import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { AgencyDto } from '@/app/common/general-types/agency.dto';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { environment } from '@/environments/environment';
import { CurrencyPipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';

interface StripePriceProduct {
  id?: string;
}

interface StripeSubscriptionItem {
  id: string;
  quantity?: number;
  plan?: { product?: string };
  price?: {
    product?: StripePriceProduct | string;
    unit_amount?: number;
    recurring?: { interval?: string };
  };
}

interface StripeSubscription {
  status: string;
  currency?: string;
  items?: { data: StripeSubscriptionItem[] };
}

@Component({
  selector: 'agency-ins-billing',
  imports: [CurrencyPipe],
  templateUrl: './billing.html',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Billing {
  readonly agency = input<AgencyDto | undefined>(undefined);

  private currentUserService = inject(CurrentUserService);

  subscription = signal<StripeSubscription | null>(null);
  isLoading = signal(false);
  private lastAgencyId: string | undefined;

  constructor() {
    effect(() => {
      const agency = this.agency();
      if (agency?.id === this.lastAgencyId) {
        return;
      }
      this.lastAgencyId = agency?.id;
      void this.loadSubscription(agency);
    });
  }

  private async loadSubscription(agency: AgencyDto | undefined): Promise<void> {
    if (!agency?.stripeCustomerId) {
      this.subscription.set(null);
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    this.subscription.set(null);
    try {
      const sub = await firstValueFrom(
        this.currentUserService.findSubscriptionForCustomer(agency.stripeCustomerId, ''),
      );
      this.subscription.set((sub as StripeSubscription | null) ?? null);
    } catch {
      this.subscription.set(null);
    } finally {
      this.isLoading.set(false);
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'active':
        return 'bg-emerald-50 text-emerald-800 ring-emerald-600/20 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-700/40';
      case 'trialing':
        return 'bg-primary/10 text-accent ring-primary/25 dark:bg-primary/25 dark:text-foreground dark:ring-primary/35';
      case 'canceled':
        return 'bg-red-50 text-red-800 ring-red-600/20 dark:bg-red-900/30 dark:text-red-200 dark:ring-red-700/40';
      case 'past_due':
        return 'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-700/40';
      default:
        return 'bg-zinc-100 text-zinc-600 ring-zinc-500/20 dark:bg-zinc-800 dark:text-zinc-300 dark:ring-zinc-600/40';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Trialling';
      case 'canceled':
        return 'Cancelled';
      case 'past_due':
        return 'Past Due';
      default:
        return status ?? 'Unknown';
    }
  }

  getProductName(productId: string): string {
    if (productId === environment.stripe.products.agency) return 'Agency Plan';
    if (productId === environment.stripe.products.old_agency) return 'Old Agency Plan';
    if (productId === environment.stripe.products.additional_brand) return 'Additional Brand';
    if (productId === environment.stripe.shaas21products.sub_essential) return 'Essential Plan';
    if (productId === environment.stripe.shaas21products.bo_ai_moderation) return 'AI Video Moderation';
    if (productId === environment.stripe.shaas21products.bo_gifting_seeding) return 'Gifting Seeding';
    if (productId === environment.stripe.shaas21products.bo_affiliates_shopify) return 'Affiliates Shopify';
    if (productId === environment.stripe.shaas21products.bo_affiliates_rakuten) return 'Affiliates Rakuten';
    if (productId === environment.stripe.shaas21products.bo_monitoring) return 'Social Monitoring';
    return 'Bolt-on';
  }

  private productId(item: StripeSubscriptionItem): string {
    const product = item.price?.product ?? item.plan?.product;
    if (typeof product === 'string') {
      return product;
    }
    return product?.id ?? '';
  }

  isBaseSub(item: StripeSubscriptionItem): boolean {
    const id = this.productId(item);
    return (
      id === environment.stripe.products.agency ||
      id === environment.stripe.products.old_agency ||
      id === environment.stripe.shaas21products.sub_essential
    );
  }

  basePlanName(sub: StripeSubscription): string {
    const base = (sub.items?.data ?? []).find((item) => this.isBaseSub(item));
    return base ? this.getProductName(this.productId(base)) : 'Agency Plan';
  }

  boltOns(sub: StripeSubscription): StripeSubscriptionItem[] {
    return (sub.items?.data ?? []).filter((item) => !this.isBaseSub(item));
  }

  getBoltOnPrice(item: StripeSubscriptionItem): number {
    const quantity = typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1;
    return ((item.price?.unit_amount ?? 0) / 100) * quantity;
  }

  getBillingPeriod(item: StripeSubscriptionItem): string {
    return item.price?.recurring?.interval === 'month' ? 'Monthly' : 'Yearly';
  }

  getCurrencyCode(sub: StripeSubscription): string {
    return (sub.currency ?? 'gbp').toUpperCase();
  }

  boltOnName(item: StripeSubscriptionItem): string {
    return this.getProductName(this.productId(item));
  }
}
